import Photo from "./Photo";
import Navigation, {TabType} from "./Navigation";
import Description from "./Description";
import Reviews from "./Reviews";
import Warranty from "./Warranty";
import TechDoc from "./TechDoc";
import BundleIncludes from "./BundleIncludes";
import {useQuery} from "@tanstack/react-query";
import {getProduct} from "../../api/catalog";
import {Link, useParams} from "react-router-dom";
import {Helmet} from "react-helmet-async";
import Money from "../core/Money";
import {useContext, useEffect, useState} from "react";
import Specification from "./Specification";
import BreadcrumbInterface from "../../interfaces/core/BreadcrumbInterface";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import ProductSlider from "../catalog/ProductSlider";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import useFavorite from "../../utils/useFavorite";
import useComparison from "../../utils/useComparison";
import useBasket from "../../utils/useBasket";
import useViewedProducts from "../../utils/useViewedProducts";
import useYandexMetrika from "../../utils/useYandexMetrika";
import MoneyInterface from "../../interfaces/core/MoneyInterface";
import {ProductPropertyValueInterface} from "../../interfaces/product/ProductPropertyValueInterface";
import NoPhoto from "./NoPhoto";
import {shouldHideWarranty} from "./warrantyVisibility";
import {getStockThresholdLabel, shouldShowMskStock, hasAvailableStock, hasProductStock} from "../../utils/stockLabel";
import WaitStatus from "./WaitStatus";
import {groupRelationProductsByType} from "../../utils/relationProducts";
import {getProductSeo} from "../../utils/seo";

type QueryDataParams = {
    categoryId: string
    productId: string
    slug: string
}
interface Props {
    productId?: string // товар не из адреса страницы
    inModal?: boolean // в модальном окне (консультант): без SEO, хлебных крошек, общего лоадера и слайдеров
}

export default function Product({productId: propProductId, inModal = false}: Props = {}) {

    const queryData = useParams<QueryDataParams>();
    const productId = propProductId ?? queryData.productId;

    const [selectedTab, setSelectedTab] = useState<TabType>(TabType.DESCRIPTION);

    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const {reachGoal} = useYandexMetrika();

    const {data: response, status} = useQuery({
        queryKey: [productId, state.customer?.id],
        queryFn: () => getProduct(productId as string, state.customer),
        //@ts-ignore
        retry: !window.NOSCRIPT ? false : 1
    })

    useEffect(() => {
        if (inModal) {
            return;
        }
        switch (status) {
            case 'pending':
                dispatch({type: ReducerActionType.LOAD})
                return;
            case 'error':
                dispatch({type: ReducerActionType.ERROR, payload: 'При загрузке товаров возникли ошибки'})
                return;
            case 'success':
                dispatch({type: ReducerActionType.SUCCESS})
                return;
        }
    }, [status]);

    const {inFavorites, toggleFavorite} = useFavorite(response?.product || {id: 0} as ProductInterface);
    const {inComparison, toggleComparison} = useComparison(response?.product || {id: 0} as ProductInterface);
    const {addToBasket, inBasket, getQuantity, updateQuantity} = useBasket(response?.product || {id: 0} as ProductInterface);
    const {viewedProducts} = useViewedProducts(response?.product?.id);
    const hideWarranty = shouldHideWarranty(response?.product);

    useEffect(() => {
        if (hideWarranty && selectedTab === TabType.WARRANTY) {
            setSelectedTab(TabType.DESCRIPTION);
        }
    }, [hideWarranty, selectedTab]);

    const getProductProperties = () => {
        let out: ProductPropertyValueInterface[] = [];
        const propertyDictionary = response?.product.properties_dictionary || {};
        Object.keys(response?.product.properties || {}).forEach(key => {
            if (propertyDictionary[key] && propertyDictionary[key].show_in_characteristics) {
                out.push({
                    id: parseInt(key),
                    name: propertyDictionary[key].name,
                    is_link: propertyDictionary[key].is_link,
                    order: propertyDictionary[key].characteristics_order || 0,
                    value: response?.product.properties[parseInt(key)]?.join(', ') || ''
                });
            }
        });

        // Сортировка по characteristics_order, затем по name
        out.sort((a, b) => {
            const orderA = propertyDictionary[a.id]?.characteristics_order;
            const orderB = propertyDictionary[b.id]?.characteristics_order;

            // Если у обоих есть characteristics_order, сортируем по нему
            if (orderA !== undefined && orderB !== undefined) {
                return orderA - orderB;
            }

            // Если только у одного есть characteristics_order, он идет первым
            if (orderA !== undefined) return -1;
            if (orderB !== undefined) return 1;

            // Если у обоих нет characteristics_order, сортируем по name
            return a.name.localeCompare(b.name);
        });

        return out
    }

    useEffect(() => {
        if (response?.category.name) {
            let breadcrumbs: BreadcrumbInterface[] = [];

            if ((response?.parentCategories?.length || 0) > 0) {
                breadcrumbs = response?.parentCategories?.map((item) => {
                    return {
                        name: item.name,
                        url: '/catalog/list/' + item.catalog_code + '/' + item.human_url
                    }
                }) as BreadcrumbInterface[];
            }
            if (response?.product.name) {
                breadcrumbs.push({
                    name: response?.product.name + ', ' + response?.product.brand_name,
                    url: ''
                })
            }

            if (!inModal) {
                dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: breadcrumbs})
            }

            reachGoal('product', {
                id: response.product.id,
                name: response.product.name,
                customer_id: state.customer?.id,
                customer_name: state.customer?.name,
            });
        }
    }, [response?.product.id]);

    const parentCategory = response?.parentCategories?.[response.parentCategories.length - 1];

    const getProfit = (price1: MoneyInterface, price2: MoneyInterface): MoneyInterface => {
        return {amount: (price1.amount - price2.amount), currency: price1.currency};
    }

    const seo = response?.product?.name ? getProductSeo(response.product) : null;
    const hasStock = response?.product ? hasProductStock(response.product) : undefined;

    if (inModal && status === 'pending') {
        return <div className="text-center text-muted py-5">Загрузка…</div>;
    }
    if (inModal && status === 'error') {
        return <div className="text-center text-danger py-5">Не удалось загрузить товар</div>;
    }

    return (<>
        {seo && !inModal && <Helmet>
            <title>{seo.title}</title>
            <meta name="description" content={seo.description}/>
        </Helmet>}
        <div className="product">
            <div className={inModal ? '' : 'container'}>
                <div className="bs-head product__head">
                    <h1>{response?.product.name}, {response?.product.brand_name} </h1>
                </div>
                <div>
                    <div className="d-flex flex-wrap align-items-center gap-3 gap-xl-4">
                        <div className="right-product__mark">
                            Артикул: {response?.product.articul}
                        </div>
                        <div className="right-product__mark">
                            Код: {response?.product.code}
                        </div>
                        <div className="right-product__mark">
                            Бренд: {response?.product.brand_name}
                        </div>
                        {response?.product.is_new && <span className="badge bg-primary fs-6">Новинка</span>}
                        {response?.product.is_egrci && <img src="https://www.pride.ru/images/egrci.svg" alt="ЕГРСИ" title="ЕГРСИ"
                                          style={{maxWidth: '60px', maxHeight: '60px'}}/>}
                    </div>
                </div>
                <div className="gx-md-6 row gy-5 gy-md-0 product__content">
                    {response?.product?.additional_params ?
                        <Photo additionalParams={response?.product.additional_params}
                               productName={response?.product.name}
                               hasStock={hasStock}/> : <NoPhoto hasStock={hasStock}/>}
                    <div
                        className="d-flex flex-column align-items-start col-12 col-md-7 flex-lg-row gap-4 product__right right-product">
                        <div className="d-flex gap-2 w-100 w-md-auto">
                            <div className="d-flex flex-column right-product__desc">
                                <div className="d-flex flex-column right-product__desc-row">
                                    {getProductProperties().slice(0, 5).map(property => (
                                        <div key={property.id}
                                             className="d-flex align-items-center gap-1 right-product__desc-item">
                                            <p className="opacity-25">{property.name}</p>
                                            <div className="line"></div>
                                            <p>{property.is_link && parentCategory ? <Link to={'/catalog/list/' + parentCategory.catalog_code + '/' + parentCategory.human_url + '?filters=f'+ property.id +'%3A' + property.value}>{property.value}</Link> : property.value}</p>
                                        </div>
                                    ))}
                                    {(response?.product?.length ?? 0) > 0 &&
                                        <div className="d-flex align-items-center gap-1 right-product__desc-item">
                                            <p className="opacity-25">Размеры</p>
                                            <div className="line"></div>
                                            <p>{response?.product.length}х{response?.product.width}х{response?.product.height} мм</p>
                                        </div>}
                                    {(response?.product?.weight ?? 0) > 0 &&
                                        <div className="d-flex align-items-center gap-1 right-product__desc-item">
                                            <p className="opacity-25">Вес</p>
                                            <div className="line"></div>
                                            <p>{response?.product.weight} кг</p>
                                        </div>}
                                    {getProductProperties().length > 0 &&
                                        <div className="d-flex align-items-center gap-1 right-product__desc-item">
                                            <a href="#prod-bot" onClick={() => {
                                                setSelectedTab(TabType.SPECIFICATION)
                                            }}>Перейти к характеристикам</a>
                                        </div>}

                                </div>
                                {/*<div className="right-product__logo">*/}
                                {/*    <img src="images/desc-logo.svg" alt=""/>*/}
                                {/*</div>*/}
                            </div>
                        </div>
                        <div className="d-flex flex-column gap-3 gap-xl-4 p-3 p-xl-4 prod-info right-product__info">
                            <div className="d-flex flex-column prod-info__body">
                                <div className="fw-bold prod-info__title">
                                    Информация о получении заказа:
                                </div>
                                {response?.product.client_price && (() => {
                                    const showMsk = shouldShowMskStock(response.product.base_stock?.stock_id, response.product.client_stock?.stock_id);
                                    const mskCnt = response.product.base_stock?.cnt_for_sale ?? 0;
                                    const mskHasStock = mskCnt > 0;
                                    return (
                                        <div className="d-flex justify-content-between flex-lg-column justify-content-lg-start prod-info__check">
                                            <div className="d-flex prod-info__check-i">
                                                {response.product.has_client_stock && <picture>
                                                    <source srcSet="/images/check-icon1.webp" type="image/webp"/>
                                                    <img src="/images/check-icon1.png" alt=""/>
                                                </picture>}
                                                <div className="d-flex flex-column">
                                                    <p>{response.product.has_client_stock ? 'В наличии (' + state.customer?.stock_id + ')' : 'Нет в наличии (' + state.customer?.stock_id + ')'}</p>
                                                    <p><span className="t-orange"> {response.product.client_smart_stock}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            {showMsk && <div className="d-flex prod-info__check-i">
                                                {mskHasStock && <picture>
                                                    <source srcSet="/images/check-icon1.webp" type="image/webp"/>
                                                    <img src="/images/check-icon1.png" alt=""/>
                                                </picture>}
                                                <div className="d-flex flex-column">
                                                    <p>{mskHasStock ? 'В наличии (МСК)' : 'Нет в наличии (МСК)'}</p>
                                                    {mskHasStock && <p><span className="t-orange">{getStockThresholdLabel(mskCnt)}</span></p>}
                                                </div>
                                            </div>}
                                            {response.product.arrival_date && <div className="d-flex prod-info__check-i">
                                                <picture>
                                                    <source srcSet="images/check-icon2.webp" type="image/webp"/>
                                                    <img src="images/check-icon2.png" alt=""/></picture>
                                                <div className="d-flex flex-column">
                                                    <p>Следующее поступление:</p>
                                                    <p><span className="t-orange">{response.product.arrival_date},</span> <span
                                                        className="t-gray">со склада</span>
                                                    </p>
                                                </div>
                                            </div>}
                                        </div>
                                    );
                                })()}
                                {!response?.product.client_price && <div
                                    className="d-flex justify-content-between flex-lg-column justify-content-lg-start prod-info__check">
                                    <div className="d-flex prod-info__check-i">
                                        {response?.product.has_stock && <picture>
                                            <source srcSet="/images/check-icon1.webp" type="image/webp"/>
                                            <img src="/images/check-icon1.png" alt=""/>
                                        </picture>}
                                        <div className="d-flex flex-column">
                                            <p>{response?.product.has_stock ? '(ИМ) В наличии' : '(ИМ) Нет в наличии'}</p>
                                            <p><span className="t-orange"> {response?.product.smart_stock}</span></p>
                                        </div>
                                    </div>
                                    {response?.product.arrival_date && <div className="d-flex prod-info__check-i">
                                        <picture>
                                            <source srcSet="images/check-icon2.webp" type="image/webp"/>
                                            <img src="images/check-icon2.png" alt=""/></picture>
                                        <div className="d-flex flex-column">
                                            <p>Следующее поступление:</p>
                                            <p><span className="t-orange">{response?.product.arrival_date},</span> <span
                                                className="t-gray">со склада</span>
                                            </p>
                                        </div>
                                    </div>}
                                </div>}
                                {response?.product.client_price && <div
                                    className="d-flex align-items-end align-items-lg-start flex-lg-column flex-row-reverse prod-info__price">
                                    <p className="t-orange big">{response?.product.client_price &&
                                        <Money value={response?.product.client_price}/>}</p>
                                    {(response?.product?.client_base_price?.amount || 0) > (response?.product?.client_price?.amount || 0) &&
                                        <p className="cross">
                                            {response?.product?.client_base_price &&
                                                <Money value={response.product.client_base_price}/>}
                                        </p>
                                    }
                                </div>}
                                {!response?.product.client_price && response?.product.current_price && <div
                                    className="d-flex align-items-end align-items-lg-start flex-lg-column flex-row-reverse prod-info__price">
                                    <p className="t-orange big">{response?.product.current_price &&
                                        <Money value={response?.product.current_price}/>}</p>
                                    {(response?.product?.base_price?.amount || 0) > (response?.product?.current_price?.amount || 0) &&
                                        <p className="cross">
                                            {response?.product?.base_price &&
                                                <Money value={response.product.base_price}/>}
                                        </p>
                                    }
                                </div>}
                                {response?.product.client_price && <div
                                    className="d-flex align-items-end align-items-lg-start flex-lg-column flex-row-reverse text-muted">
                                    <div>{response?.product.current_price &&
                                        <div>Розничная цена: <Money value={response?.product.current_price}/>
                                        </div>}</div>
                                    <div>{response?.product.current_price && <div>Ваша выгода: <Money
                                        value={getProfit(response?.product.current_price, response?.product.client_price)}/>
                                    </div>}</div>
                                </div>}
                            </div>


                            {response?.product?.client_price && hasAvailableStock(response.product.has_client_stock, response.product.base_stock?.cnt_for_sale) && !inBasket() &&
                                <div className="btn btn-primary"
                                     onClick={() => {
                                         if (!response?.product.id.toString()) return
                                         addToBasket()
                                     }}>
                                    <svg width="28" height="29" viewBox="0 0 28 29" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5.83415 17.0669H21.1592C22.3408 17.0669 22.9316 17.0669 23.4019 16.8466C23.8163 16.6525 24.1644 16.3409 24.403 15.9506C24.6739 15.5074 24.7392 14.9202 24.8697 13.7458L25.5522 7.6033C25.592 7.24459 25.6119 7.06524 25.5543 6.92641C25.5036 6.80449 25.4131 6.70336 25.2975 6.63955C25.1659 6.56689 24.9854 6.56689 24.6245 6.56689H5.25081M2.33398 3.06689H3.7905C4.09922 3.06689 4.25359 3.06689 4.37435 3.12561C4.48067 3.17729 4.56878 3.26006 4.62699 3.36295C4.69313 3.47982 4.70275 3.63388 4.72201 3.94201L5.77929 20.8584C5.79855 21.1666 5.80818 21.3206 5.87431 21.4375C5.93253 21.5404 6.02063 21.6232 6.12695 21.6748C6.24772 21.7336 6.40208 21.7336 6.71081 21.7336H22.1673M8.75065 25.8169H8.76232M19.2507 25.8169H19.2623M9.33398 25.8169C9.33398 26.1391 9.07282 26.4002 8.75065 26.4002C8.42848 26.4002 8.16732 26.1391 8.16732 25.8169C8.16732 25.4947 8.42848 25.2336 8.75065 25.2336C9.07282 25.2336 9.33398 25.4947 9.33398 25.8169ZM19.834 25.8169C19.834 26.1391 19.5728 26.4002 19.2507 26.4002C18.9285 26.4002 18.6673 26.1391 18.6673 25.8169C18.6673 25.4947 18.9285 25.2336 19.2507 25.2336C19.5728 25.2336 19.834 25.4947 19.834 25.8169Z"
                                            stroke="white" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round"/>
                                    </svg>
                                    <span>Добавить</span>
                                </div>}

                            {response?.product?.client_price && hasAvailableStock(response.product.has_client_stock, response.product.base_stock?.cnt_for_sale) && inBasket() &&
                                <div className="d-flex gap-3 w-100 align-items-stretch">
                                    <div className="quantity d-flex align-items-stretch">
                                        <div className="quantity__row">
                                            <button className="quantity__number-minus" type="button" onClick={() => {
                                                const currentQuantity = getQuantity();
                                                if (currentQuantity > 1) {
                                                    updateQuantity(currentQuantity - 1);
                                                }
                                            }}>
                                                -
                                            </button>
                                            <input type="number" min="1" value={getQuantity()}
                                                   className="quantity__input" onChange={(e) => {
                                                const newQuantity = parseInt(e.target.value) || 1;
                                                updateQuantity(newQuantity);
                                            }}/>
                                            <button className="quantity__number-plus" type="button" onClick={() => {
                                                updateQuantity(getQuantity() + 1);
                                            }}>
                                                +
                                            </button>
                                        </div>
                                    </div>
                                    <Link to="/basket" className="btn btn-secondary flex-grow-1 d-flex align-items-center justify-content-center">
                                        <svg width="28" height="29" viewBox="0 0 28 29" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M5.83415 17.0669H21.1592C22.3408 17.0669 22.9316 17.0669 23.4019 16.8466C23.8163 16.6525 24.1644 16.3409 24.403 15.9506C24.6739 15.5074 24.7392 14.9202 24.8697 13.7458L25.5522 7.6033C25.592 7.24459 25.6119 7.06524 25.5543 6.92641C25.5036 6.80449 25.4131 6.70336 25.2975 6.63955C25.1659 6.56689 24.9854 6.56689 24.6245 6.56689H5.25081M2.33398 3.06689H3.7905C4.09922 3.06689 4.25359 3.06689 4.37435 3.12561C4.48067 3.17729 4.56878 3.26006 4.62699 3.36295C4.69313 3.47982 4.70275 3.63388 4.72201 3.94201L5.77929 20.8584C5.79855 21.1666 5.80818 21.3206 5.87431 21.4375C5.93253 21.5404 6.02063 21.6232 6.12695 21.6748C6.24772 21.7336 6.40208 21.7336 6.71081 21.7336H22.1673M8.75065 25.8169H8.76232M19.2507 25.8169H19.2623M9.33398 25.8169C9.33398 26.1391 9.07282 26.4002 8.75065 26.4002C8.42848 26.4002 8.16732 26.1391 8.16732 25.8169C8.16732 25.4947 8.42848 25.2336 8.75065 25.2336C9.07282 25.2336 9.33398 25.4947 9.33398 25.8169ZM19.834 25.8169C19.834 26.1391 19.5728 26.4002 19.2507 26.4002C18.9285 26.4002 18.6673 26.1391 18.6673 25.8169C18.6673 25.4947 18.9285 25.2336 19.2507 25.2336C19.5728 25.2336 19.834 25.4947 19.834 25.8169Z"
                                                stroke="white" strokeWidth="2" strokeLinecap="round"
                                                strokeLinejoin="round"/>
                                        </svg>
                                    </Link>

                                </div>}

                            {response?.product?.client_price && !hasAvailableStock(response.product.has_client_stock, response.product.base_stock?.cnt_for_sale) && state.customer &&
                                <WaitStatus
                                    customer={state.customer}
                                    code={response.product.code}
                                    productName={response.product.name}
                                    stockId={state.customer.stock_id}
                                />}

                            <div className={'btn gray prod-info__stat ' + (inFavorites() && '_active')} style={{padding: "0.375rem"}} onClick={() => {
                                if (!response?.product.id.toString()) return
                                toggleFavorite()
                            }}>
                                <svg width="30" height="31" viewBox="0 0 30 31" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                          d="M14.9914 7.15317C12.4923 4.2314 8.32468 3.44545 5.19337 6.12091C2.06205 8.79638 1.62121 13.2696 4.08025 16.4339C6.12477 19.0648 12.3122 24.6135 14.3401 26.4094C14.567 26.6104 14.6804 26.7108 14.8128 26.7503C14.9283 26.7848 15.0546 26.7848 15.1701 26.7503C15.3024 26.7108 15.4159 26.6104 15.6428 26.4094C17.6707 24.6135 23.8581 19.0648 25.9026 16.4339C28.3617 13.2696 27.9746 8.76823 24.7895 6.12091C21.6044 3.47359 17.4906 4.2314 14.9914 7.15317Z"
                                          stroke="#898A8C" strokeWidth="2.5" strokeLinecap="round"
                                          strokeLinejoin="round"/>
                                </svg>
                                <span>В избранно{inFavorites() ? 'м' : 'е'}</span>
                            </div>
                            <div className={'btn btn-secondary prod-info__stat ' }  onClick={() => {
                                if (!response?.product.id.toString()) return
                                toggleComparison()
                            }}>

                                <span>{inComparison() ? 'Удалить из сравнения' : 'Сравнить' }</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="product__bottom" id="prod-bot">
                    <Navigation selected={selectedTab} setSelected={setSelectedTab} hideTabs={hideWarranty ? [TabType.WARRANTY] : []}/>
                    {response?.product && <div className="tab-content">
                        {selectedTab === TabType.DESCRIPTION && <Description product={response.product}/>}
                        {selectedTab === TabType.SPECIFICATION &&
                            <Specification product={response.product} properties={getProductProperties()} parentCategory={parentCategory}/>}
                        {selectedTab === TabType.REVIEWS && <Reviews/>}
                        {selectedTab === TabType.WARRANTY && !hideWarranty && <Warranty product={response.product}/>}
                        {selectedTab === TabType.TECHNICAL_DOCUMENTATION && <TechDoc product={response.product}/>}
                        {selectedTab === TabType.BUNDLE_INCLUDES && <BundleIncludes product={response.product}/>}
                    </div>}
                </div>
            </div>
        </div>

        {!inModal && groupRelationProductsByType(response?.relationProducts ?? []).map(group =>
            <ProductSlider
                key={group.typeId}
                products={group.products}
                header={group.label}
            />
        )}
        {!inModal && viewedProducts.length > 0 &&
            <ProductSlider products={viewedProducts} header={'Вы смотрели ранее'}/>}

    </>)
}